import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useSavedResources, unsaveResource } from '../utils/savedResources';
import ResourceCardList from './ResourceCardList';
import CompareOverlay, { compareGrid } from './compare/CompareOverlay';

const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const sectionHeading = {
  fontFamily: FONT_HEAD, fontSize: '1.05rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '10px',
};

// Full-screen overlay listing everything saved via the ☆ toggle on School/Union Finder cards --
// no account needed, backed entirely by localStorage (see utils/savedResources). Reuses
// ResourceCardList so saved cards render identically to the ones they were saved from, same
// overlay pattern as TradeHub (fixed inset:0, sticky header with a close button), now shared
// with every other comparison view via CompareOverlay.
export default function SavedResourcesPanel({ onClose }) {
  const { t } = useTranslation();
  const { saved, clear } = useSavedResources();

  // Saved cards are full localStorage snapshots taken at save time, so a school/union
  // deactivated afterward would otherwise keep showing here indefinitely with no
  // indication -- resource_directory's RLS policy only exposes is_active rows to the
  // public role, so any saved id missing from this query is no longer live (deactivated
  // or deleted, doesn't matter which).
  const [inactiveIds, setInactiveIds] = useState(() => new Set());
  useEffect(() => {
    if (saved.length === 0) { setInactiveIds(new Set()); return; }
    let cancelled = false;
    (async () => {
      const ids = saved.map(r => r.id);
      const { data } = await supabase.from('resource_directory').select('id').in('id', ids);
      if (cancelled) return;
      const activeIds = new Set((data || []).map(r => r.id));
      setInactiveIds(new Set(ids.filter(id => !activeIds.has(id))));
    })();
    return () => { cancelled = true; };
  }, [saved]);

  const schools = saved.filter(r => r.type === 'school' && !inactiveIds.has(r.id));
  const unions = saved.filter(r => r.type === 'union' && !inactiveIds.has(r.id));
  const noLongerAvailable = saved.filter(r => inactiveIds.has(r.id));

  return (
    <CompareOverlay
      eyebrow={t('resources.savedPanel.eyebrow')}
      title={t('resources.savedPanel.title', { count: saved.length })}
      onClose={onClose}
      onClear={saved.length > 0 ? clear : null}
      clearLabel={t('common.clearAll')}
      closeLabel={t('resources.savedPanel.closeAriaLabel')}
    >
      {saved.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
          {t('resources.savedPanel.empty')}
        </div>
      ) : (
        <>
          {schools.length > 0 && (
            <div style={{ marginBottom: unions.length > 0 ? '28px' : 0 }}>
              <p style={sectionHeading}>{t('resources.savedPanel.schoolsCount', { count: schools.length })}</p>
              <div style={compareGrid}>
                <ResourceCardList resources={schools} type="school" defaultExpanded />
              </div>
            </div>
          )}
          {unions.length > 0 && (
            <div style={{ marginBottom: noLongerAvailable.length > 0 ? '28px' : 0 }}>
              <p style={sectionHeading}>{t('resources.unionsCount', { count: unions.length })}</p>
              <div style={compareGrid}>
                <ResourceCardList resources={unions} type="union" />
              </div>
            </div>
          )}
          {noLongerAvailable.length > 0 && (
            <div>
              <p style={sectionHeading}>{t('resources.savedPanel.noLongerAvailableCount', { count: noLongerAvailable.length })}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {noLongerAvailable.map(r => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                      padding: '12px 14px', borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <div>
                      <p style={{ fontSize: '0.88rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '2px' }}>
                        {r.name}
                      </p>
                      <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.35)' }}>
                        {t('resources.savedPanel.noLongerAvailableBadge')}
                      </p>
                    </div>
                    <button
                      onClick={() => unsaveResource(r.id)}
                      style={{
                        fontSize: '0.76rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)',
                        background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px',
                        padding: '6px 10px', cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                      }}
                    >
                      {t('resources.savedPanel.removeButton')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </CompareOverlay>
  );
}
